import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/features/notifications';

import { Card, CardContent } from '@/components/ui/card';
import { Button, Icon, Typography } from '@/components';
import { useOfferSlots, useOfferMeeting, useConfirmMeeting } from '@/features/offers/calendar/hooks';
import { OfferSlot } from '@/features/offers/calendar/api/types';
import { useOnUpdateOffer } from '@/features/offers/hooks';

// ─── Types ───────────────────────────────────────────────────────────────────

type MeetingSlotsCardProps = {
  readonly offerId: string;
  readonly mode: 'seller' | 'buyer';
  readonly manualRequestMeeting?: boolean;
};

type ParsedSlot = {
  id: string;
  label: string;
  timeRange: string;
  weekNumber: number;
  isSelected: boolean;
};

type CardState = 'selecting' | 'manual_requested' | 'scheduled';

const MAX_AVAILABILITY_LENGTH = 500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseSlots(slots: OfferSlot[], selectedSlotId: string | null): ParsedSlot[] {
  return slots
    .map((slot) => {
      const startDate = new Date(slot.start_time);
      const endDate = new Date(slot.end_time);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

      const label = startDate.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

      const startHour = startDate.getHours().toString().padStart(2, '0');
      const startMin = startDate.getMinutes().toString().padStart(2, '0');
      const endHour = endDate.getHours().toString().padStart(2, '0');
      const endMin = endDate.getMinutes().toString().padStart(2, '0');

      const jan1 = new Date(startDate.getFullYear(), 0, 1);
      const days = Math.floor((startDate.getTime() - jan1.getTime()) / 86400000);
      const weekNumber = Math.ceil((days + jan1.getDay() + 1) / 7);

      return {
        id: slot.id,
        label,
        timeRange: `${startHour}:${startMin}-${endHour}:${endMin}`,
        weekNumber,
        isSelected: slot.id === selectedSlotId,
      };
    })
    .filter(Boolean) as ParsedSlot[];
}

function buildSummary(parsed: ParsedSlot[]): string {
  const windows = parsed.length;
  const weeks = new Set(parsed.map((s) => s.weekNumber)).size;

  return `${windows} window${windows !== 1 ? 's' : ''} · ${weeks} week${weeks !== 1 ? 's' : ''}`;
}

export function MeetingSlotsCard({ offerId, mode, manualRequestMeeting }: MeetingSlotsCardProps) {
  const { t } = useTranslation(['offers']);
  const { add } = useNotifications();
  const { slots, isLoading: isSlotsLoading } = useOfferSlots(offerId);
  const { meeting, isLoading: isMeetingLoading, isScheduled } = useOfferMeeting(offerId);
  const { confirmMeeting, isLoading: isConfirming } = useConfirmMeeting();
  const { updateOfferSlotManualRequest } = useOnUpdateOffer({ id: offerId });

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  // Optimistic: el prop viene del offer del padre y no se refresca hasta el refetch.
  const [requestedLocally, setRequestedLocally] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [availability, setAvailability] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);

  const confirmedSlotId = meeting?.selected_slot_id ?? null;
  const isBuyer = mode === 'buyer';
  const isManualRequested = (manualRequestMeeting ?? false) || requestedLocally;

  const cardState: CardState = isScheduled ? 'scheduled' : isManualRequested ? 'manual_requested' : 'selecting';

  const canSelect = isBuyer && cardState === 'selecting';

  const parsedSlots = useMemo(
    () => parseSlots(slots, isScheduled ? confirmedSlotId : selectedSlotId),
    [slots, isScheduled, confirmedSlotId, selectedSlotId]
  );
  const summary = useMemo(() => buildSummary(parsedSlots), [parsedSlots]);

  const confirmedSlot = useMemo(
    () => (isScheduled ? (parsedSlots.find((slot) => slot.isSelected) ?? null) : null),
    [isScheduled, parsedSlots]
  );

  const isLoading = isSlotsLoading || isMeetingLoading;

  const handleConfirm = useCallback(async () => {
    if (!selectedSlotId) return;
    try {
      await confirmMeeting({
        offer_id: offerId,
        selected_slot_id: selectedSlotId,
      }).unwrap();

      add({
        id: new Date().toString(),
        title: t('Meeting confirmed'),
        description: t('A Google Meet link has been sent to all participants.'),
      });
    } catch (error) {
      console.error('Error confirming meeting:', error);

      add({
        id: new Date().toString(),
        title: t('Error'),
        description: t('Could not confirm the meeting. Please try again.'),
      });
    }
  }, [selectedSlotId, offerId, confirmMeeting, add, t]);

  const handleManualScheduling = useCallback(() => {
    if (selectedSlotId) return;
    updateOfferSlotManualRequest({
      request_manual_scheduling: true,
      manual_scheduling_availability: availability.trim() || null,
    });
    setRequestedLocally(true);
  }, [selectedSlotId, availability, updateOfferSlotManualRequest]);

  // ─── Copy by state ───────────────────────────────────────────────────────

  const headerIconClass =
    cardState === 'scheduled' ? 'bg-emerald-600' : cardState === 'manual_requested' ? 'bg-sky-500' : 'bg-amber-500';

  const headerIconName = cardState === 'scheduled' ? 'check' : cardState === 'manual_requested' ? 'clock' : 'calendar';

  const headerTitle =
    cardState === 'scheduled'
      ? t('Kick-off meeting confirmed')
      : cardState === 'manual_requested'
        ? t('Manual scheduling requested')
        : t('Availability shared');

  const headerSubtitle =
    cardState === 'scheduled'
      ? t('The meeting has been scheduled. Check your calendar for details.')
      : cardState === 'manual_requested'
        ? t('A member of the nTeaser team will contact you to arrange a time that fits.')
        : canSelect
          ? t('Select a slot and confirm the kick-off meeting.')
          : t('You will receive an email when the kick-off is confirmed.');

  const slotsSectionTitle =
    cardState === 'scheduled' ? t('Meeting slot') : canSelect ? t('Choose a slot') : t('Slots shared');

  return (
    <Card className="mb-6 border border-emerald-200 bg-emerald-50/30 backdrop-blur-sm">
      <CardContent>
        {/* Header */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full ${headerIconClass}`}>
            <Icon name={headerIconName} className="h-6 w-6 text-white" />
          </div>
          <Typography as="h3" size="body-1" className="font-bold">
            {headerTitle}
          </Typography>
          <Typography size="body-3" className="mt-1 text-slate-500">
            {headerSubtitle}
          </Typography>
        </div>

        <hr className="border-emerald-100" />

        {/* Slots */}
        <div className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <Typography as="p" size="body-3" className="font-bold tracking-wide uppercase">
              {slotsSectionTitle}
            </Typography>
            {!isLoading && cardState === 'selecting' && (
              <Typography size="body-3" className="text-slate-500">
                {summary}
              </Typography>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Typography size="body-3" className="text-slate-400">
                {t('Loading slots...')}
              </Typography>
            </div>
          ) : isScheduled && !confirmedSlot ? (
            /* Asigned date but without slot */
            <Typography size="body-3" className="text-slate-400 italic">
              {t('The meeting date will appear here shortly.')}
            </Typography>
          ) : parsedSlots.length === 0 ? (
            <Typography size="body-3" className="text-slate-400 italic">
              {t('No slots found')}
            </Typography>
          ) : (
            <div className="space-y-2">
              {parsedSlots.map((slot) => {
                // En modo scheduled, solo mostrar el slot confirmado
                if (isScheduled && !slot.isSelected) return null;

                const isActive = slot.isSelected;
                const isClickable = canSelect && !isConfirming;

                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={!isClickable}
                    onClick={() => isClickable && setSelectedSlotId(slot.id === selectedSlotId ? null : slot.id)}
                    className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 transition-all ${
                      isActive
                        ? 'border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600'
                        : isClickable
                          ? 'cursor-pointer border-slate-200 bg-white hover:border-emerald-300'
                          : 'border-slate-200 bg-white'
                    } disabled:cursor-default`}
                  >
                    <div className="flex items-center gap-3">
                      {canSelect && (
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                            isActive ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                          }`}
                        >
                          {isActive && <Icon name="check" className="h-3 w-3 text-white" />}
                        </div>
                      )}
                      {isScheduled && isActive && <Icon name="check" className="h-5 w-5 text-emerald-600" />}
                      <Typography size="body-3" className="font-semibold text-emerald-700">
                        {slot.label}
                      </Typography>
                    </div>
                    <Typography size="body-3" className="text-slate-700">
                      {slot.timeRange}
                    </Typography>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions (buyer only, before confirmed and not manual request) */}
        {canSelect && (
          <>
            <div className="mt-4">
              <Button
                size="sm"
                color="primary"
                className="w-full"
                onClick={handleConfirm}
                disabled={!selectedSlotId || isConfirming}
                icon="arrow-right"
              >
                {isConfirming ? t('Confirming...') : t('Confirm kick-off meeting')}
              </Button>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => setIsManualOpen((open) => !open)}
                aria-expanded={isManualOpen}
                aria-controls="manual-scheduling-panel"
                className="text-left"
              >
                <Typography as="span" size="body-3" className="text-slate-600">
                  {t('None of these times work for you?')}{' '}
                  <span className="font-bold text-emerald-700 underline">{t('Click here')}</span>
                </Typography>
              </button>

              {isManualOpen && (
                <div
                  id="manual-scheduling-panel"
                  className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-3"
                >
                  <label htmlFor="manual-scheduling-availability" className="mt-3 block">
                    <Typography as="span" size="body-3" className="mb-1 block font-bold">
                      {t('When would suit you? (optional)')}
                    </Typography>
                  </label>
                  <textarea
                    id="manual-scheduling-availability"
                    rows={3}
                    maxLength={MAX_AVAILABILITY_LENGTH}
                    value={availability}
                    onChange={(event) => setAvailability(event.target.value)}
                    placeholder={t('e.g. Tuesdays and Wednesdays after 3pm CET')}
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 focus:outline-none"
                  />
                  <Typography size="body-3" className="mt-1 text-right text-slate-400" aria-live="polite">
                    {availability.length}/{MAX_AVAILABILITY_LENGTH}
                  </Typography>

                  <Button
                    size="sm"
                    color="ghostPrimary"
                    className="mt-2 w-full"
                    onClick={handleManualScheduling}
                    disabled={isConfirming || isRequesting || !!selectedSlotId}
                  >
                    {isRequesting ? t('Sending...') : t('Request manual scheduling')}
                  </Button>

                  {!!selectedSlotId ? (
                    <Typography size="body-3" className="mt-2 text-slate-500">
                      {t('You have a slot selected. Confirm it above, or deselect it to request manual scheduling.')}
                    </Typography>
                  ) : (
                    <Typography size="body-3" className="mt-2 text-slate-500">
                      {t('Our team will contact you to arrange a time that fits.')}
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Manual request send, meeting without date */}
        {cardState === 'manual_requested' && (
          <>
            <hr className="mt-4 border-emerald-100" />
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="clock" className="h-4 w-4" />
                <span className="font-bold text-black">45 min</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="video" className="h-4 w-4" />
                <span className="font-bold text-black">Google Meet: </span>
                <span>{t('shared once the time is agreed')}</span>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
              <Typography size="body-3" className="text-sky-900">
                {t(
                  'None of the proposed times worked, so the nTeaser team will contact you to agree on a new one. The date and the Google Meet link will appear here as soon as the meeting is set.'
                )}
              </Typography>
            </div>
          </>
        )}

        {/* Selected meeting */}
        {cardState === 'scheduled' && (
          <>
            <hr className="mt-4 border-emerald-100" />
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="clock" className="h-4 w-4" />
                <span className="font-bold text-black">45 min</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="video" className="h-4 w-4" />
                <span className="font-bold text-black">Google Meet: </span>
                {meeting?.google_meet_link ? (
                  <a
                    href={meeting.google_meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline hover:text-emerald-800"
                  >
                    {t('Join meeting')}
                  </a>
                ) : (
                  <span>{t('shared soon by nTeaser')}</span>
                )}
              </div>
            </div>
            {isManualRequested && (
              <Typography size="body-3" className="mt-3 text-slate-500">
                {t('This meeting was arranged manually by the nTeaser team.')}
              </Typography>
            )}
          </>
        )}

        {/* Next steps (when not yet scheduled) */}
        {cardState === 'selecting' && (
          <>
            <hr className="mt-4 border-emerald-100" />
            <div className="mt-4">
              <Typography as="p" size="body-3" className="mb-3 font-bold tracking-wide uppercase">
                {t('Next steps: Kick-off meeting')}
              </Typography>
              <Typography size="body-3" className="text-slate-600">
                {t(
                  'A Kick-off Meeting (KOM) is being scheduled for both parties to meet, discuss the project, the Expression of Interest received, your expectations, and coordinate next steps. nTeaser will support you throughout the whole process.'
                )}
              </Typography>
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Icon name="clock" className="h-4 w-4" />
                  <span className="font-bold text-black">45 min</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Icon name="video" className="h-4 w-4" />
                  <span className="font-bold text-black">Google Meet Link: </span>
                  <span>{t('shared soon by nTeaser')}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
